import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { MergeInput, MergeSide } from "./merge";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export interface DuplicatePerson extends MergeSide {
  readonly email: string | null;
  readonly phone: string | null;
}

export interface DuplicatePair {
  /** Stable across refetches: the two ids in the order the database returns them. */
  readonly key: string;
  readonly a: DuplicatePerson;
  readonly b: DuplicatePerson;
  readonly reasons: readonly string[];
}

/**
 * Likely duplicates in this church, strongest match first. The database finds the pairs under row-level security, so
 * a person the reader cannot see is never in one; this then reads the two people's names and primary contacts.
 */
export function useDuplicates() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["duplicates", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<DuplicatePair[]> => {
      const pairs = await supabase.rpc("find_duplicate_people", { p_tenant_id: tenantId ?? "" });
      if (pairs.error) throw pairs.error;
      if (pairs.data.length === 0) return [];
      const ids = [...new Set(pairs.data.flatMap((pair) => [pair.person_a, pair.person_b]))];
      const people = await supabase
        .from("people")
        .select(
          "id, first_name, last_name, preferred_name, birthdate, created_at, person_contacts(method, value, is_primary)",
        )
        .eq("tenant_id", tenantId ?? "")
        .in("id", ids);
      if (people.error) throw people.error;
      const byId = new Map(
        people.data.map((row) => {
          const primary = (method: "email" | "phone") => {
            const ofMethod = row.person_contacts.filter((c) => c.method === method);
            return (ofMethod.find((c) => c.is_primary) ?? ofMethod[0])?.value ?? null;
          };
          const person: DuplicatePerson = {
            id: row.id,
            firstName: row.first_name,
            lastName: row.last_name,
            preferredName: row.preferred_name,
            birthdate: row.birthdate,
            createdAt: row.created_at,
            email: primary("email"),
            phone: primary("phone"),
          };
          return [row.id, person];
        }),
      );
      return pairs.data.flatMap((pair) => {
        const a = byId.get(pair.person_a);
        const b = byId.get(pair.person_b);
        // A person merged or deleted between the two reads simply drops out of the list.
        return a === undefined || b === undefined
          ? []
          : [{ key: `${pair.person_a}:${pair.person_b}`, a, b, reasons: pair.reasons }];
      });
    },
  });
}

function useDuplicateInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["duplicates", tenantId] });
    await client.invalidateQueries({ queryKey: ["people", tenantId] });
    await client.invalidateQueries({ queryKey: ["person", tenantId] });
    await client.invalidateQueries({ queryKey: ["households", tenantId] });
    await client.invalidateQueries({ queryKey: ["household", tenantId] });
    await client.invalidateQueries({ queryKey: ["tags", tenantId] });
  };
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

export function useDismissDuplicate() {
  const supabase = useSupabase();
  const invalidate = useDuplicateInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly a: string; readonly b: string }) => {
      const { error } = await supabase.rpc("dismiss_duplicate", {
        p_person_a: input.a,
        p_person_b: input.b,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

/** Merges one record into another and returns what moved, by kind. */
export function useMergePeople() {
  const supabase = useSupabase();
  const invalidate = useDuplicateInvalidation();
  return useMutation({
    mutationFn: async (input: MergeInput): Promise<Record<string, number>> => {
      const { data, error } = await supabase.rpc("merge_people", {
        p_keep_id: input.keepId,
        p_merge_id: input.mergeId,
        p_first_name: input.firstName,
        p_last_name: input.lastName,
        // The generated types mark every argument without a default as a non-null string, but these two are
        // nullable in SQL: a record may have no preferred name or birthdate, and a merge may choose that.
        p_preferred_name: input.preferredName as string,
        p_birthdate: input.birthdate as string,
      });
      failed(error);
      // The function returns an object of counts by kind (ADR 0030).
      return data as Record<string, number>;
    },
    onSuccess: invalidate,
  });
}
